/**
 * Planar polygon math for site object footprints (spec §14, extension).
 *
 * Everything here works in a single 2D plane — the ground — using `{ x, z }` because that is what
 * the rest of the app calls the horizontal axes. It is deliberately free of Three.js and of the
 * `SiteObject` record, so the predicates the editor, the compiler, and the geometry builder all
 * depend on can be tested as arithmetic.
 *
 * ## Orientation
 *
 * A polygon is stored **counter-clockwise by signed area in (x, z)**, and {@link normalizeRing}
 * enforces that on the way in. Two consequences worth stating, because both are easy to get
 * backwards and neither is visible until a shadow looks wrong:
 *
 * - `(x, z)` with `+Y` up is a *left-handed* pair. A ring that is counter-clockwise by the signed
 *   area formula therefore triangulates to triangles whose normal points **down**, so a cap that
 *   must face `+Y` emits its indices reversed. See `SiteObjectGeometry`.
 * - The local→world map used across the app is `X = lx·cos + lz·sin`, `Z = −lx·sin + lz·cos`, which
 *   is a rotation by `−rotationY` in the standard sense. {@link toWorldPoint} and
 *   {@link toLocalPoint} are exact inverses of each other; before this module existed the inverse
 *   was written by flipping the angle *inside* the same formula, which applies the forward map a
 *   second time and rotates the keep-out by `2·rotationY` instead of undoing it.
 */

export interface Point2 {
  x: number;
  z: number;
}

/** A closed ring. The final vertex is *not* repeated — the edge back to `[0]` is implied. */
export type Ring = Point2[];

/** Rings below this area are treated as degenerate and rejected by {@link isValidRing}. */
const MIN_RING_AREA = 1e-6;

/* --------------------------------- Construction --------------------------------- */

/** The centred axis-aligned rectangle a width/depth pair describes. */
export function rectangleRing(widthMeters: number, depthMeters: number): Ring {
  const hw = widthMeters / 2;
  const hd = depthMeters / 2;
  // Wound to a positive signed area, matching the invariant `normalizeRing` enforces.
  return [
    { x: -hw, z: -hd },
    { x: hw, z: -hd },
    { x: hw, z: hd },
    { x: -hw, z: hd },
  ];
}

/**
 * Twice the signed area. Positive means counter-clockwise in (x, z). Kept separate from
 * {@link ringArea} because the sign is the useful part when deciding winding.
 */
export function signedDoubleArea(ring: readonly Point2[]): number {
  let total = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i] as Point2;
    const b = ring[(i + 1) % ring.length] as Point2;
    total += a.x * b.z - b.x * a.z;
  }
  return total;
}

/** Unsigned area in square metres. */
export function ringArea(ring: readonly Point2[]): number {
  return Math.abs(signedDoubleArea(ring)) / 2;
}

/** Whether a ring is usable: at least a triangle, finite, and enclosing real area. */
export function isValidRing(ring: readonly Point2[] | undefined | null): ring is Ring {
  // `Array.isArray` widens a readonly array to `any[]`, so test the shape without it.
  if (ring === undefined || ring === null || ring.length < 3) return false;
  for (const point of ring) {
    if (point === null || typeof point !== 'object') return false;
    if (!Number.isFinite(point.x) || !Number.isFinite(point.z)) return false;
  }
  return ringArea(ring) > MIN_RING_AREA;
}

/**
 * Copy a ring into canonical form: counter-clockwise, with consecutive duplicate vertices dropped.
 *
 * Duplicates matter beyond tidiness — a zero-length edge has no direction, so it produces a NaN
 * normal in the extruded wall and a degenerate triangle in the cap.
 */
export function normalizeRing(ring: readonly Point2[]): Ring {
  const out: Ring = [];
  for (const point of ring) {
    const previous = out[out.length - 1];
    if (previous && nearlyEqual(previous, point)) continue;
    out.push({ x: point.x, z: point.z });
  }
  const first = out[0];
  const last = out[out.length - 1];
  if (out.length > 1 && first && last && nearlyEqual(first, last)) out.pop();
  if (signedDoubleArea(out) < 0) out.reverse();
  return out;
}

function nearlyEqual(a: Point2, b: Point2): boolean {
  return Math.abs(a.x - b.x) < 1e-9 && Math.abs(a.z - b.z) < 1e-9;
}

/* ---------------------------------- Predicates ---------------------------------- */

/**
 * Even-odd point-in-polygon. Points exactly on an edge are not guaranteed either way, which is why
 * {@link ringContains} tests distance as well rather than relying on this alone.
 */
export function pointInRing(ring: readonly Point2[], x: number, z: number): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const a = ring[i] as Point2;
    const b = ring[j] as Point2;
    const straddles = a.z > z !== b.z > z;
    if (!straddles) continue;
    const crossingX = ((b.x - a.x) * (z - a.z)) / (b.z - a.z) + a.x;
    if (x < crossingX) inside = !inside;
  }
  return inside;
}

/** Shortest distance from a point to the ring's boundary. Zero on the boundary itself. */
export function distanceToRing(ring: readonly Point2[], x: number, z: number): number {
  let best = Infinity;
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i] as Point2;
    const b = ring[(i + 1) % ring.length] as Point2;
    best = Math.min(best, distanceToSegment(x, z, a, b));
  }
  return best;
}

function distanceToSegment(x: number, z: number, a: Point2, b: Point2): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const lengthSquared = dx * dx + dz * dz;
  if (lengthSquared < 1e-12) return Math.hypot(x - a.x, z - a.z);
  const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / lengthSquared));
  return Math.hypot(x - (a.x + t * dx), z - (a.z + t * dz));
}

/**
 * Whether a point falls inside the ring **grown outward by `margin`**.
 *
 * The grown region is the true offset polygon — inside, or within `margin` of the boundary —
 * rather than a scaled copy. Scaling would grow a long thin building far more along its length than
 * across its width, which is exactly wrong for the clearance a keep-out is expressing.
 *
 * The region is **closed**: a point exactly on the boundary is contained even at `margin` 0. That
 * matches the rectangle test this replaced, and it is the answer a keep-out wants — a plant whose
 * centre lands precisely on a wall line is inside the wall, not beside it.
 */
export function ringContains(ring: readonly Point2[], x: number, z: number, margin = 0): boolean {
  if (pointInRing(ring, x, z)) return true;
  return distanceToRing(ring, x, z) <= Math.max(margin, BOUNDARY_EPSILON);
}

/** Tolerance that makes {@link ringContains} closed rather than open on the boundary itself. */
const BOUNDARY_EPSILON = 1e-9;

/** Axis-aligned bounds. */
export function ringBounds(ring: readonly Point2[]): {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
} {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const point of ring) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minZ = Math.min(minZ, point.z);
    maxZ = Math.max(maxZ, point.z);
  }
  return { minX, maxX, minZ, maxZ };
}

/** Area-weighted centroid. Falls back to the vertex mean for a degenerate ring. */
export function ringCentroid(ring: readonly Point2[]): Point2 {
  const doubleArea = signedDoubleArea(ring);
  if (Math.abs(doubleArea) < MIN_RING_AREA) {
    let x = 0;
    let z = 0;
    for (const point of ring) {
      x += point.x;
      z += point.z;
    }
    return { x: x / Math.max(1, ring.length), z: z / Math.max(1, ring.length) };
  }
  let x = 0;
  let z = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i] as Point2;
    const b = ring[(i + 1) % ring.length] as Point2;
    const cross = a.x * b.z - b.x * a.z;
    x += (a.x + b.x) * cross;
    z += (a.z + b.z) * cross;
  }
  return { x: x / (3 * doubleArea), z: z / (3 * doubleArea) };
}

/* ------------------------------- Oriented bounds ------------------------------- */

export interface OrientedBox {
  center: Point2;
  /** Half-extent along the box's own first axis. */
  halfWidth: number;
  /** Half-extent along the box's own second axis. */
  halfDepth: number;
  /** Rotation of the box's first axis away from local +X, in radians. */
  angle: number;
}

/**
 * Minimum-area enclosing rectangle, by rotating calipers over the convex hull.
 *
 * This is what lets a pitched roof sit over an arbitrary mass: the roof is solved in the box's
 * frame, so a wing rotated 20° off the main house still gets a ridge running along its own length
 * rather than along the world axes.
 */
export function orientedBox(ring: readonly Point2[]): OrientedBox {
  const hull = convexHull(ring);
  if (hull.length < 3) {
    const bounds = ringBounds(ring);
    return {
      center: { x: (bounds.minX + bounds.maxX) / 2, z: (bounds.minZ + bounds.maxZ) / 2 },
      halfWidth: (bounds.maxX - bounds.minX) / 2,
      halfDepth: (bounds.maxZ - bounds.minZ) / 2,
      angle: 0,
    };
  }

  let best: OrientedBox | null = null;
  let bestArea = Infinity;

  // The minimum-area rectangle always has one side flush with a hull edge, so only hull edge
  // directions need testing rather than a sweep over all angles.
  for (let i = 0; i < hull.length; i += 1) {
    const a = hull[i] as Point2;
    const b = hull[(i + 1) % hull.length] as Point2;
    const edgeAngle = Math.atan2(b.z - a.z, b.x - a.x);
    const cos = Math.cos(-edgeAngle);
    const sin = Math.sin(-edgeAngle);

    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    for (const point of hull) {
      const u = point.x * cos - point.z * sin;
      const v = point.x * sin + point.z * cos;
      minU = Math.min(minU, u);
      maxU = Math.max(maxU, u);
      minV = Math.min(minV, v);
      maxV = Math.max(maxV, v);
    }

    const area = (maxU - minU) * (maxV - minV);
    if (area >= bestArea) continue;
    bestArea = area;

    // Centre back out of the edge frame.
    const cu = (minU + maxU) / 2;
    const cv = (minV + maxV) / 2;
    best = {
      center: { x: cu * cos + cv * sin, z: -cu * sin + cv * cos },
      halfWidth: (maxU - minU) / 2,
      halfDepth: (maxV - minV) / 2,
      angle: edgeAngle,
    };
  }

  return best as OrientedBox;
}

/**
 * How nearly a ring fills its own minimum-area rectangle, in `[0, 1]`.
 *
 * `SiteObjectGeometry` uses this to decide whether a pitched roof is meaningful: a ridge over an
 * L-shaped mass covers the notch as well as the building, so an outline-following flat cap is the
 * honest answer and a pitched roof is expressed by splitting the mass in two.
 */
export function rectangularity(ring: readonly Point2[]): number {
  const box = orientedBox(ring);
  const boxArea = box.halfWidth * box.halfDepth * 4;
  if (boxArea < MIN_RING_AREA) return 0;
  return Math.min(1, ringArea(ring) / boxArea);
}

/** Andrew's monotone chain. Returns the hull counter-clockwise in (x, z). */
export function convexHull(points: readonly Point2[]): Point2[] {
  const sorted = [...points].sort((a, b) => (a.x === b.x ? a.z - b.z : a.x - b.x));
  if (sorted.length < 3) return sorted;

  const cross = (o: Point2, a: Point2, b: Point2): number =>
    (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);

  const build = (input: readonly Point2[]): Point2[] => {
    const chain: Point2[] = [];
    for (const point of input) {
      while (
        chain.length >= 2 &&
        cross(chain[chain.length - 2] as Point2, chain[chain.length - 1] as Point2, point) <= 0
      ) {
        chain.pop();
      }
      chain.push(point);
    }
    chain.pop();
    return chain;
  };

  return [...build(sorted), ...build([...sorted].reverse())];
}

/* --------------------------------- Transforms --------------------------------- */

/**
 * Local footprint coordinates → world.
 *
 * This is the convention the whole app already renders with (`SiteObjectWorld` sets
 * `group.rotation.y = rotationY`, and Three.js rotates local `(lx, lz)` to exactly this).
 */
export function toWorldPoint(
  local: Point2,
  origin: Point2,
  rotationY: number,
): Point2 {
  const cos = Math.cos(rotationY);
  const sin = Math.sin(rotationY);
  return {
    x: origin.x + local.x * cos + local.z * sin,
    z: origin.z - local.x * sin + local.z * cos,
  };
}

/**
 * World → local footprint coordinates: the exact inverse of {@link toWorldPoint}.
 *
 * Derived rather than guessed. With `c = cos(rotationY)`, `s = sin(rotationY)` the forward map is
 * `X = lx·c + lz·s`, `Z = −lx·s + lz·c`; solving that pair gives `lx = dx·c − dz·s` and
 * `lz = dx·s + dz·c`. Substituting `−rotationY` into the *forward* formula does **not** produce
 * this, it reapplies the forward map — the error that made rotated keep-outs miss their buildings.
 */
export function toLocalPoint(world: Point2, origin: Point2, rotationY: number): Point2 {
  const cos = Math.cos(rotationY);
  const sin = Math.sin(rotationY);
  const dx = world.x - origin.x;
  const dz = world.z - origin.z;
  return { x: dx * cos - dz * sin, z: dx * sin + dz * cos };
}

/** A whole ring mapped into world space. */
export function ringToWorld(ring: readonly Point2[], origin: Point2, rotationY: number): Ring {
  return ring.map((point) => toWorldPoint(point, origin, rotationY));
}
